import { useEffect, useRef, useState, type RefObject } from 'react';
import { Mail, Plus, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEmailTemplateStore } from '../store/emailTemplateStore';
import { SlideOver } from '../components/common/SlideOver';
import { BodyEditor, MERGE_FIELDS, TemplatePreview } from '../components/campaigns';
import type { EmailTemplate, TemplateSlots } from '../types/campaign';

// Inserts `{{token}}` at the current caret position of a plain text input,
// mirroring BodyEditor's merge-field select for fields that aren't rich text.
function insertTokenAtCursor(current: string, token: string, input: HTMLInputElement | null): { text: string; cursor: number } {
  const pos = input?.selectionStart ?? current.length;
  const inserted = `{{${token}}}`;
  return { text: `${current.slice(0, pos)}${inserted}${current.slice(pos)}`, cursor: pos + inserted.length };
}

const emptyForm = { name: '', description: '', subject: '', headline: '', bodyHtml: '', ctaLabel: '', ctaUrl: '', imageUrl: '', preheader: '' };
type FormField = 'subject' | 'headline';

// A CTA/image URL slot may carry a {{merge}} token instead of a literal URL
// (the real value only exists once a recipient's mergeValues are known) —
// mirrors packages/backend/src/validators/emailTemplate.ts's urlOrTokenSchema
// so an invalid value is caught here rather than only after a failed save.
const TOKEN_PRESENT_RE = /\{\{\s*[a-z_]+\s*(?:\|[^}]*)?\}\}/;
function isValidUrlOrToken(value: string, schemes: string[]): boolean {
  if (!value) return true;
  if (TOKEN_PRESENT_RE.test(value)) return true;
  try {
    return schemes.includes(new URL(value).protocol.replace(':', ''));
  } catch {
    return false;
  }
}

/**
 * Template library: lists the active email templates and provides a
 * create/edit form (name, subject, headline, body, CTA, image, preheader)
 * with a live preview of the rendered email. Route wiring and nav entry are
 * added separately from this page.
 */
export const EmailTemplates = () => {
  const { templates, isLoading, error, fetchTemplates, saveTemplate } = useEmailTemplateStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const subjectRef = useRef<HTMLInputElement>(null);
  const headlineRef = useRef<HTMLInputElement>(null);
  const fieldRefs: Record<FormField, RefObject<HTMLInputElement>> = { subject: subjectRef, headline: headlineRef };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openNew = () => {
    setEditingTemplate(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  const openEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description ?? '',
      subject: template.subject,
      headline: template.slots.headline ?? '',
      bodyHtml: template.slots.bodyHtml,
      ctaLabel: template.slots.ctaLabel ?? '',
      ctaUrl: template.slots.ctaUrl ?? '',
      imageUrl: template.slots.imageUrl ?? '',
      preheader: template.slots.preheader ?? '',
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingTemplate(null);
  };

  function saveDisabledReason(): string | null {
    if (!form.name.trim()) return 'Add a name to save';
    if (!form.subject.trim()) return 'Add a subject to save';
    if (!isValidUrlOrToken(form.ctaUrl, ['http', 'https'])) return 'Fix invalid CTA URL';
    if (!isValidUrlOrToken(form.imageUrl, ['https'])) return 'Fix invalid image URL';
    return null;
  }

  const insertToken = (field: FormField, token: string) => {
    const ref = fieldRefs[field];
    const { text, cursor } = insertTokenAtCursor(form[field], token, ref.current);
    setForm((f) => ({ ...f, [field]: text }));
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(cursor, cursor);
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Preservation of an existing headline across an edit happens on open
    // (openEdit seeds form.headline from the template), not here — the
    // backend PATCH replaces the slots JSON wholesale, so omitting the key
    // clears it, and the form must be able to clear it like any other slot.
    const slots: TemplateSlots = {
      bodyHtml: form.bodyHtml,
      ...(form.headline ? { headline: form.headline } : {}),
      ...(form.preheader ? { preheader: form.preheader } : {}),
      ...(form.ctaLabel ? { ctaLabel: form.ctaLabel } : {}),
      ...(form.ctaUrl ? { ctaUrl: form.ctaUrl } : {}),
      ...(form.imageUrl ? { imageUrl: form.imageUrl } : {}),
    };
    const result = await saveTemplate(
      { name: form.name, description: form.description, subject: form.subject, slots },
      editingTemplate?.id,
    );
    setIsSaving(false);
    if (result) {
      toast.success('Template saved');
      closeForm();
      fetchTemplates();
    } else {
      // Read the store fresh rather than the `error` closed over at render
      // time: saveTemplate just set it, but this closure captured whatever
      // value was current when handleSave was invoked, which is stale by
      // the time the await above resolves.
      toast.error(useEmailTemplateStore.getState().error ?? 'Failed to save template');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Templates</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {templates.length} template{templates.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <button onClick={openNew} className="btn-primary flex-shrink-0 whitespace-nowrap">
            <Plus className="h-4 w-4 mr-1.5" />
            New template
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <span className="text-red-800 dark:text-red-300">{error}</span>
          </div>
        )}

        {/* Loading state */}
        {isLoading && templates.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && templates.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Mail className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No templates yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first email template to start building campaigns</p>
            <button onClick={openNew} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New template
            </button>
          </div>
        )}

        {/* Template list */}
        {templates.length > 0 && (
          <div className="space-y-3">
            {templates.map((template) => (
              <div key={template.id} className="card p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{template.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{template.subject}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Updated {new Date(template.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => openEdit(template)} className="btn-secondary flex-shrink-0">
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create/edit form */}
        <SlideOver
          isOpen={isFormOpen}
          onClose={closeForm}
          title={editingTemplate ? 'Edit template' : 'New template'}
          wide
          footer={
            <div className="flex items-center justify-end gap-3 flex-wrap">
              {!isSaving && saveDisabledReason() && (
                <p className="w-full text-xs text-amber-700 dark:text-amber-400 text-right">{saveDisabledReason()}</p>
              )}
              <button type="button" onClick={closeForm} className="btn-secondary" disabled={isSaving}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                disabled={isSaving || !!saveDisabledReason()}
                title={saveDisabledReason() ?? undefined}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          }
        >
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
            {/* Form fields — stack in one column; two-pane grid kicks in at lg. */}
            <div className="space-y-4">
              <div>
                <label className="label mb-1">Name</label>
                <input
                  type="text"
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Fall outreach"
                />
              </div>

              <div>
                <label className="label mb-1">Description</label>
                <textarea
                  className="input"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional internal note about when to use this template"
                />
              </div>

              <div>
                <label className="label mb-1">Subject</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={subjectRef}
                    type="text"
                    className="input"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="e.g. {{first_name}}, a quick note about {{team_name}}"
                  />
                  <select
                    className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-2 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0"
                    value=""
                    onChange={(e) => { if (e.target.value) insertToken('subject', e.target.value); }}
                    aria-label="Insert merge field into subject"
                  >
                    <option value="">Insert field…</option>
                    {MERGE_FIELDS.map((f) => <option key={f} value={f}>{`{{${f}}}`}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="label mb-1">Headline</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={headlineRef}
                    type="text"
                    className="input"
                    maxLength={200}
                    value={form.headline}
                    onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))}
                    placeholder="e.g. A quick update about {{team_name}}"
                  />
                  <select
                    className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-2 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0"
                    value=""
                    onChange={(e) => { if (e.target.value) insertToken('headline', e.target.value); }}
                    aria-label="Insert merge field into headline"
                  >
                    <option value="">Insert field…</option>
                    {MERGE_FIELDS.map((f) => <option key={f} value={f}>{`{{${f}}}`}</option>)}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{form.headline.length}/200 characters</p>
              </div>

              <div>
                <label className="label mb-1">Body</label>
                <BodyEditor value={form.bodyHtml} onChange={(html) => setForm((f) => ({ ...f, bodyHtml: html }))} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label mb-1">CTA label</label>
                  <input
                    type="text"
                    className="input"
                    value={form.ctaLabel}
                    onChange={(e) => setForm((f) => ({ ...f, ctaLabel: e.target.value }))}
                    placeholder="e.g. Schedule a call"
                  />
                </div>
                <div>
                  <label className="label mb-1">CTA URL</label>
                  <input
                    type="text"
                    className="input"
                    value={form.ctaUrl}
                    onChange={(e) => setForm((f) => ({ ...f, ctaUrl: e.target.value }))}
                    placeholder="https://…"
                  />
                </div>
              </div>

              <div>
                <label className="label mb-1">Image URL</label>
                <input
                  type="text"
                  className="input"
                  value={form.imageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  placeholder="https://…"
                />
              </div>

              <div>
                <label className="label mb-1">Preheader</label>
                <input
                  type="text"
                  className="input"
                  value={form.preheader}
                  onChange={(e) => setForm((f) => ({ ...f, preheader: e.target.value }))}
                  placeholder="Short preview text shown next to the subject in most inboxes"
                />
              </div>
            </div>

            {/* Live preview — its own scroll region, pinned in view via
                sticky while the form column above scrolls past it. */}
            <div className="lg:sticky lg:top-0 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto">
              <p className="label mb-1">Preview</p>
              <TemplatePreview
                subject={form.subject}
                slots={{
                  bodyHtml: form.bodyHtml,
                  headline: form.headline || undefined,
                  preheader: form.preheader || undefined,
                  ctaLabel: form.ctaLabel || undefined,
                  ctaUrl: form.ctaUrl || undefined,
                  imageUrl: form.imageUrl || undefined,
                }}
              />
            </div>
          </div>
        </SlideOver>
      </div>
    </div>
  );
};
